export const SQRT3 = Math.sqrt(3);

export const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export const normalizeAngle = (degrees) => {
  let value = ((degrees + 180) % 360 + 360) % 360 - 180;
  if (Object.is(value, -0)) value = 0;
  return value;
};

export const rotatePoint = (point, degrees) => {
  const radians = degrees * Math.PI / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: point.x * cos - point.y * sin,
    y: point.x * sin + point.y * cos,
  };
};

export const axialToWorld = (q, r, grid) => {
  const size = grid.sizePx;
  const lattice = grid.orientation === 'flat'
    ? { x: size * 1.5 * q, y: size * SQRT3 * (r + q / 2) }
    : { x: size * SQRT3 * (q + r / 2), y: size * 1.5 * r };
  const rotated = rotatePoint(lattice, grid.rotationDeg || 0);
  return { x: rotated.x + grid.offsetX, y: rotated.y + grid.offsetY };
};

export const worldToFractionalAxial = (point, grid) => {
  const local = rotatePoint({
    x: point.x - grid.offsetX,
    y: point.y - grid.offsetY,
  }, -(grid.rotationDeg || 0));
  const size = grid.sizePx;
  if (grid.orientation === 'flat') {
    return {
      q: (2 / 3 * local.x) / size,
      r: (-1 / 3 * local.x + SQRT3 / 3 * local.y) / size,
    };
  }
  return {
    q: (SQRT3 / 3 * local.x - 1 / 3 * local.y) / size,
    r: (2 / 3 * local.y) / size,
  };
};

export const roundAxial = ({ q, r }) => {
  const x = q;
  const z = r;
  const y = -x - z;
  let rx = Math.round(x);
  let ry = Math.round(y);
  let rz = Math.round(z);
  const xDiff = Math.abs(rx - x);
  const yDiff = Math.abs(ry - y);
  const zDiff = Math.abs(rz - z);
  if (xDiff > yDiff && xDiff > zDiff) rx = -ry - rz;
  else if (yDiff > zDiff) ry = -rx - rz;
  else rz = -rx - ry;
  return { q: rx, r: rz };
};

export const worldToAxial = (point, grid) => roundAxial(worldToFractionalAxial(point, grid));

export const hexKey = (q, r) => `${q},${r}`;

export const parseHexKey = (key) => {
  const [q, r] = key.split(',').map(Number);
  return { q, r };
};

export const axialDisk = ({ q, r }, radius = 0) => {
  const cells = [];
  for (let dq = -radius; dq <= radius; dq += 1) {
    const minimumDr = Math.max(-radius, -dq - radius);
    const maximumDr = Math.min(radius, -dq + radius);
    for (let dr = minimumDr; dr <= maximumDr; dr += 1) {
      cells.push({ q: q + dq, r: r + dr });
    }
  }
  return cells;
};

export const axialDistance = (first, second) => (
  Math.abs(first.q - second.q)
  + Math.abs(first.q + first.r - second.q - second.r)
  + Math.abs(first.r - second.r)
) / 2;

// Return every adjacent cell crossed between two axial coordinates. This keeps
// drawing strokes continuous even when the browser emits sparse pointer moves.
export const axialLine = (first, second) => {
  const distance = axialDistance(first, second);
  if (distance === 0) return [{ q: first.q, r: first.r }];
  const cells = [];
  for (let index = 0; index <= distance; index += 1) {
    const ratio = index / distance;
    const cell = roundAxial({
      q: first.q + (second.q - first.q) * ratio,
      r: first.r + (second.r - first.r) * ratio,
    });
    if (!cells.some(({ q, r }) => q === cell.q && r === cell.r)) cells.push(cell);
  }
  return cells;
};

export const isPointInMap = (point, width, height, padding = 0) => (
  point.x >= -padding && point.y >= -padding
  && point.x <= width + padding && point.y <= height + padding
);

export const getHexCorners = (center, grid) => {
  const startAngle = grid.orientation === 'flat' ? 0 : -90;
  return Array.from({ length: 6 }, (_, index) => {
    const angle = (startAngle + index * 60 + (grid.rotationDeg || 0)) * Math.PI / 180;
    return {
      x: center.x + grid.sizePx * Math.cos(angle),
      y: center.y + grid.sizePx * Math.sin(angle),
    };
  });
};

const axialRangeForWorldPoints = (points, grid, padding = 2) => {
  const axial = points.map((point) => worldToFractionalAxial(point, grid));
  return {
    qMin: Math.floor(Math.min(...axial.map((point) => point.q))) - padding,
    qMax: Math.ceil(Math.max(...axial.map((point) => point.q))) + padding,
    rMin: Math.floor(Math.min(...axial.map((point) => point.r))) - padding,
    rMax: Math.ceil(Math.max(...axial.map((point) => point.r))) + padding,
  };
};

export const getMapAxialBounds = (grid, width, height) => axialRangeForWorldPoints([
  { x: 0, y: 0 }, { x: width, y: 0 }, { x: width, y: height }, { x: 0, y: height },
], grid, 0);

export const getVisibleAxialRange = (grid, view, viewport) => {
  const toWorld = ({ x, y }) => ({
    x: (x - view.x) / view.scale,
    y: (y - view.y) / view.scale,
  });
  return axialRangeForWorldPoints([
    toWorld({ x: 0, y: 0 }),
    toWorld({ x: viewport.width, y: 0 }),
    toWorld({ x: viewport.width, y: viewport.height }),
    toWorld({ x: 0, y: viewport.height }),
  ], grid, 2);
};

const toLetters = (value) => {
  let number = Math.max(1, value);
  let result = '';
  while (number > 0) {
    number -= 1;
    result = String.fromCharCode(65 + (number % 26)) + result;
    number = Math.floor(number / 26);
  }
  return result;
};

export const formatHexId = (q, r, bounds, numbering = 'four_digit') => {
  const column = q - bounds.qMin + 1;
  const row = r - bounds.rMin + 1;
  if (numbering === 'alpha_numeric') return `${toLetters(column)}${row}`;
  if (numbering === 'continuous') {
    const columnCount = bounds.qMax - bounds.qMin + 1;
    return String((row - 1) * columnCount + column);
  }
  return `${String(column).padStart(2, '0')}${String(row).padStart(2, '0')}`;
};

export const centerSpacingPx = (grid) => SQRT3 * grid.sizePx;

export const calibrateGridFromTwoPoints = (first, second, orientation) => {
  const dx = second.x - first.x;
  const dy = second.y - first.y;
  const distance = Math.hypot(dx, dy);
  if (distance < 4) return null;
  const vectorAngle = Math.atan2(dy, dx) * 180 / Math.PI;
  const referenceAngle = orientation === 'flat' ? 30 : 0;
  const rotationDeg = ((vectorAngle - referenceAngle + 30) % 60 + 60) % 60 - 30;
  return {
    sizePx: clamp(distance / SQRT3, 8, 500),
    offsetX: first.x,
    offsetY: first.y,
    rotationDeg,
  };
};

export const scaleFromHexDistance = (distanceMeters, grid) => distanceMeters / centerSpacingPx(grid);

export const formatDistance = (meters, distanceUnit, locale = 'fr') => {
  if (!Number.isFinite(meters)) return '—';
  if (distanceUnit === 'mi') {
    const miles = meters / 1609.344;
    return `${miles.toLocaleString(locale, { maximumFractionDigits: miles < 10 ? 2 : 1 })} mi`;
  }
  if (meters < 1000) return `${Math.round(meters).toLocaleString(locale)} m`;
  const kilometers = meters / 1000;
  return `${kilometers.toLocaleString(locale, { maximumFractionDigits: kilometers < 10 ? 2 : 1 })} km`;
};

const niceNumber = (value) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  const niceFraction = fraction < 1.5 ? 1 : fraction < 3.5 ? 2 : fraction < 7.5 ? 5 : 10;
  return niceFraction * 10 ** exponent;
};

export const getScaleBar = (metersPerPixel, viewScale, targetScreenPixels = 140) => {
  if (!metersPerPixel || !viewScale) return null;
  const meters = niceNumber(targetScreenPixels / viewScale * metersPerPixel);
  return { meters, screenPixels: meters / metersPerPixel * viewScale };
};
